export type AXONEventMap = {
  page_view: AXONPageViewEvent;
  view_item: AXONViewItemEvent;
  add_to_cart: AXONAddToCartEvent;
  begin_checkout: AXONBeginCheckoutEvent;
  purchase: AXONPurchaseEvent;
  add_payment_info: AXONAddPaymentInfoEvent;
  generate_lead: AXONGenerateLeadEvent;
  login: AXONLoginEvent;
  sign_up: AXONSignUpEvent;
  search: AXONSearchEvent;
  subscribe: AXONSubscribeEvent;
  remove_from_cart: AXONRemoveFromCartEvent;
};

// Payment Types
// https://developers.applovin.com/en/audience-plus/events-and-objects
export type AXONPaymentType =
  | 'credit_card'
  | 'deferred'
  | 'redeemable'
  | 'payment_on_delivery'
  | 'wallet'
  | 'other';

// Category IDs
// https://developers.applovin.com/en/audience-plus/events-and-objects#category-ids
export enum AXONCategory {
  CARBON_MONOXIDE_DETECTORS = 2164,
  HOME_ALARM_SYSTEMS = 3873,
  MOTION_SENSORS = 2161,
  SECURITY_SYSTEM_SENSORS = 365,
  SMOKE_DETECTORS = 1471,
  SURVEILLANCE_CAMERAS = 362,
  WATER_AND_FLOOD_DETECTORS = 1306,
}

// Base AXON Item Object
export type AXONItem = {
  item_variant_id?: string;
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  image_url?: string;
  item_category_id?: AXONCategory;
  affiliation?: string;
  discount?: number;
  item_brand?: string;
};

// Base event interface with common properties
interface BaseAXONEvent {
  currency: 'USD' | string;
  items: AXONItem[];
}

// Required Events
export type AXONPageViewEvent = Record<string, never>;

export interface AXONViewItemEvent extends BaseAXONEvent {
  value?: number;
}

export interface AXONAddToCartEvent extends BaseAXONEvent {
  // The AppLovin docs show this isn't required, but recommended.
  // We usually have the value of the cart, so we're going to require it.
  value: number;
}

export interface AXONBeginCheckoutEvent extends BaseAXONEvent {
  value: number;
}

export interface AXONPurchaseEvent extends BaseAXONEvent {
  transaction_id: string;
  value: number;
  // Shipping cost associated with the transaction
  shipping: number;
  // Tax cost associated with a transaction
  tax: number;
  user_id?: string;
}

// Recommended Events
export interface AXONAddPaymentInfoEvent extends BaseAXONEvent {
  payment_type?: AXONPaymentType;
  value: number;
}

export interface AXONGenerateLeadEvent {
  currency?: string;
  user_id?: string;
  value?: number;
}

export interface AXONLoginEvent {
  user_id?: string;
}

export interface AXONSignUpEvent {
  method?: string;
  user_id?: string;
}

export interface AXONSearchEvent {
  search_term: string;
  results?: AXONItem[];
}

export interface AXONSubscribeEvent {
  currency?: string;
  user_id?: string;
  value?: number;
}

export interface AXONRemoveFromCartEvent extends BaseAXONEvent {
  value?: number;
}

// Type for all possible AXON events
export type AXONEventType =
  // Required Events ⬇️
  | 'page_view'
  | 'view_item'
  | 'add_to_cart'
  | 'begin_checkout'
  | 'purchase'
  // Recommended Events ⬇️
  | 'add_payment_info'
  | 'generate_lead'
  | 'login'
  | 'sign_up'
  | 'search'
  | 'subscribe'
  | 'remove_from_cart';
